import { useCallback } from "react";
import { useSearchParams } from "react-router";
import {
  parseCalendarDate,
  parseKnownValues,
  parsePageIndex,
  parseSort,
  setOrDelete,
  SortDirection,
  withListPage,
  withListSearch,
  withListSort,
} from "../../UI/Table/listUrlParams.ts";
import {
  WithdrawalRequestSource,
  WithdrawalRequestStatus,
} from "../../SpeakingClassService/Withdrawal/WithdrawalRequestDTO.ts";
import {
  DEFAULT_WITHDRAWAL_REQUESTS_SORT,
  WITHDRAWAL_REQUEST_SORT_FIELDS,
  WITHDRAWAL_REQUESTS_PAGE_SIZE,
  WithdrawalRequestSortField,
  WithdrawalRequestsFilters,
  WithdrawalRequestsListQuery,
} from "../../SpeakingClassService/Withdrawal/WithdrawalRequestsListQuery.ts";

const FILTER_PARAMS = [
  "status",
  "source",
  "requestedFrom",
  "requestedTo",
  "purchasedFrom",
  "purchasedTo",
];

function parseWithdrawalRequestsListQuery(
  params: URLSearchParams,
): WithdrawalRequestsListQuery {
  return {
    search: params.get("search") || undefined,
    statuses: parseKnownValues(
      params.get("status"),
      Object.values(WithdrawalRequestStatus),
    ),
    sources: parseKnownValues(
      params.get("source"),
      Object.values(WithdrawalRequestSource),
    ),
    requestedFrom: parseCalendarDate(params.get("requestedFrom")),
    requestedTo: parseCalendarDate(params.get("requestedTo")),
    purchasedFrom: parseCalendarDate(params.get("purchasedFrom")),
    purchasedTo: parseCalendarDate(params.get("purchasedTo")),
    ...parseSort(
      params.get("sort"),
      WITHDRAWAL_REQUEST_SORT_FIELDS,
      DEFAULT_WITHDRAWAL_REQUESTS_SORT,
    ),
    page: parsePageIndex(params.get("page")),
    pageSize: WITHDRAWAL_REQUESTS_PAGE_SIZE,
  };
}

function withWithdrawalRequestsFilters(
  current: URLSearchParams,
  filters: WithdrawalRequestsFilters,
): URLSearchParams {
  const params = new URLSearchParams(current);
  FILTER_PARAMS.forEach((param) => params.delete(param));
  setOrDelete(params, "status", filters.statuses?.join(","));
  setOrDelete(params, "source", filters.sources?.join(","));
  setOrDelete(params, "requestedFrom", filters.requestedFrom);
  setOrDelete(params, "requestedTo", filters.requestedTo);
  setOrDelete(params, "purchasedFrom", filters.purchasedFrom);
  setOrDelete(params, "purchasedTo", filters.purchasedTo);
  params.delete("page");
  return params;
}

export function useWithdrawalRequestsListUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = parseWithdrawalRequestsListQuery(searchParams);

  const setSearch = useCallback(
    (search: string) =>
      setSearchParams((current) => withListSearch(current, search)),
    [setSearchParams],
  );

  const setSort = useCallback(
    (sortBy: WithdrawalRequestSortField, sortDirection: SortDirection) =>
      setSearchParams((current) =>
        withListSort(
          current,
          { sortBy, sortDirection },
          DEFAULT_WITHDRAWAL_REQUESTS_SORT,
        ),
      ),
    [setSearchParams],
  );

  const setFilters = useCallback(
    (filters: WithdrawalRequestsFilters) =>
      setSearchParams((current) =>
        withWithdrawalRequestsFilters(current, filters),
      ),
    [setSearchParams],
  );

  const toPage = useCallback(
    (pageIndex: number) =>
      setSearchParams((current) => withListPage(current, pageIndex)),
    [setSearchParams],
  );

  return { query, setSearch, setSort, setFilters, toPage };
}
